import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { JsonForms } from '@jsonforms/react';
import type { JsonSchema } from '@jsonforms/core';
import { materialCells, materialRenderers } from '@jsonforms/material-renderers';
import { customRenderers } from '@/components/forms/renderers';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { nexoJsonFormsAjv } from '@/lib/jsonFormsAjv';
import { useJsonFormsTranslation } from '@/hooks/useJsonFormsTranslation';
import { apiClient } from '@/lib/apiClient';
import { useEnhancedJsonForms } from '@/core/hooks/useEnhancedJsonForms';
import { useBulkRun } from './useBulkRun';
import { BulkWarningsAlert } from './BulkWarningsAlert';
import {
  addableFields,
  bulkEditRequest,
  editorFor,
  withoutField,
  type BulkEditConfig,
  type BulkEditEntry,
  type BulkEditFieldSpec,
  type BulkEditSummary,
  type BulkOperationResult,
  type BulkRowRef,
  withUdfFields,
} from '@/lib/bulkEdit';

interface BulkEditDialogProps {
  open: boolean;
  /** Entity name, for labels and translations (e.g. `Order`). */
  entity: string;
  config: BulkEditConfig;
  /** The selected rows, each with the stamp the grid loaded it with. */
  rows: BulkRowRef[];
  /** The user's field restrictions on the entity: a restricted field is not offered. */
  restrictions: ReadonlyMap<string, string>;
  onClose: () => void;
  /** The run is over: everything applied or refused. The list refreshes and reports the summary. */
  onCompleted: (summary: BulkEditSummary) => void;
}

const renderers = [...materialRenderers, ...customRenderers];

/**
 * The dialog for an entity with user-defined fields: the form's schemas with the UDFs merged in, as the detail
 * view loads them, so the dialog offers and edits them like the entity's own fields.
 */
function BulkEditDialogWithUdfs(props: BulkEditDialogProps) {
  const { enhancedSchema, enhancedUiSchema } = useEnhancedJsonForms(props.entity, 'DetailView', props.config.schema, props.config.uischema);
  const config = useMemo(
    () => withUdfFields(props.config, enhancedSchema, enhancedUiSchema),
    [props.config, enhancedSchema, enhancedUiSchema],
  );
  return <BulkEditDialogBody {...props} config={config} />;
}

/**
 * The bulk edit dialog (docs/15-designspecs/Bulk-Edit-And-Actions.md §8.2): add the fields to change one at a time,
 * set each or clear it, and run. Fields that are not added are not touched. A row whose save raises warnings
 * keeps the dialog open with the warnings grouped by rule, and "Acknowledge and run these N" re-runs exactly
 * those rows (D14). Closed, it renders nothing and loads nothing: the list renders it on every visit.
 */
export function BulkEditDialog(props: BulkEditDialogProps) {
  if (!props.open) return null;
  return props.config.udfs ? <BulkEditDialogWithUdfs {...props} /> : <BulkEditDialogBody {...props} />;
}

/** What a run sends: the fields added and their values. A re-run of the warned rows sends what the first run did. */
interface BulkEditPayload {
  entries: BulkEditEntry[];
  values: Record<string, unknown>;
}

function BulkEditDialogBody({ open, entity, config, rows, restrictions, onClose, onCompleted }: BulkEditDialogProps) {
  const { t } = useTranslation();
  const i18n = useJsonFormsTranslation(entity);
  const [entries, setEntries] = useState<BulkEditEntry[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const { running, requestErrors, warned, start, acknowledgeAndRun, close: closeRun } = useBulkRun<BulkEditPayload>(
    async (selection, acknowledgedWarnings, payload) => (await apiClient.post<BulkOperationResult>(
      config.endpoint,
      bulkEditRequest(selection, payload.entries, payload.values, config, acknowledgedWarnings),
    )).data,
    (summary) => {
      setEntries([]);
      setValues({});
      onCompleted(summary);
    },
  );
  // The picker's text, controlled so it empties once a field is added: the field shows as its own row then.
  const [pickerText, setPickerText] = useState('');

  const specs = useMemo(() => new Map(config.fields.map((f) => [f.name, f])), [config.fields]);
  // One editor per field for the life of the config: a JsonForms handed a new schema or UI schema re-initializes,
  // reports its data again and drops what is being typed — a new object each render was a render loop.
  const editors = useMemo(
    () => new Map(config.fields.map((f) => [f.name, editorFor(f, config.schema, config.uischema)])),
    [config],
  );
  const addable = addableFields(config, entries, restrictions);
  // While a run waits on its warnings the edit is held: the warned rows are re-run with what the others got.
  const locked = running || warned !== null;

  const labelOf = (spec: BulkEditFieldSpec) => {
    const anchor = spec.properties[0];
    const title = (config.schema.properties?.[anchor] as JsonSchema | undefined)?.title ?? spec.name;
    return t(`DisplayName:${entity}.${spec.name}`, {
      defaultValue: t(`DisplayName:${entity}.${anchor}`, { defaultValue: title }),
    });
  };

  const close = () => {
    setEntries([]);
    setValues({});
    closeRun(onClose);
  };

  const addField = (spec: BulkEditFieldSpec | null) => {
    if (!spec) return;
    setEntries((current) => [...current, { field: spec.name, operation: 'Set' }]);
    // A checkbox shows unchecked before it is touched: start it at false, so Set means what it shows.
    setValues((current) => ({
      ...current,
      ...Object.fromEntries(spec.properties
        .filter((p) => current[p] === undefined && (config.schema.properties?.[p] as JsonSchema | undefined)?.type === 'boolean')
        .map((p) => [p, false])),
    }));
  };

  return (
    <Dialog open={open} onClose={running ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>{t('bulkEdit.title', { count: rows.length })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('bulkEdit.intro')}
        </Typography>

        {requestErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {requestErrors.map((message) => <div key={message}>{message}</div>)}
          </Alert>
        )}

        {warned && <BulkWarningsAlert summary={warned} running={running} onAcknowledge={acknowledgeAndRun} />}

        <JsonFormsThemeProvider density="compact" variant="standard">
          {entries.map((entry) => {
            const spec = specs.get(entry.field)!;
            const editor = editors.get(entry.field)!;
            return (
              <Box key={entry.field} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Typography sx={{ width: 180, pt: 1, flexShrink: 0 }}>{labelOf(spec)}</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  disabled={locked}
                  value={entry.operation}
                  onChange={(_, operation) => operation && setEntries((current) =>
                    current.map((e) => (e.field === entry.field ? { ...e, operation } : e)))}
                >
                  <ToggleButton value="Set">{t('bulkEdit.set')}</ToggleButton>
                  {/* A required field always has a value: there is nothing to fill or clear. */}
                  <ToggleButton value="FillEmpty" disabled={locked || spec.required}>{t('bulkEdit.fillEmpty')}</ToggleButton>
                  <ToggleButton value="Clear" disabled={locked || spec.required}>{t('bulkEdit.clear')}</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {entry.operation !== 'Clear' && (
                    <JsonForms
                      schema={editor.schema}
                      uischema={editor.uischema}
                      data={values}
                      readonly={locked}
                      renderers={renderers}
                      cells={materialCells}
                      ajv={nexoJsonFormsAjv}
                      i18n={i18n}
                      onChange={({ data }) => setValues((current) => withFieldValues(current, spec, data))}
                    />
                  )}
                </Box>
                <Tooltip title={t('bulkEdit.removeField')}>
                  {/* A disabled button fires no events: the span carries the tooltip's. */}
                  <span>
                    <IconButton
                      aria-label={t('bulkEdit.removeField')}
                      disabled={locked}
                      onClick={() => setEntries((current) => withoutField(current, entry.field, config))}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </JsonFormsThemeProvider>

        <Autocomplete
          options={addable}
          getOptionLabel={labelOf}
          value={null}
          inputValue={pickerText}
          onInputChange={(_, text, reason) => setPickerText(reason === 'reset' ? '' : text)}
          onChange={(_, spec) => {
            addField(spec);
            setPickerText('');
          }}
          disabled={locked || addable.length === 0}
          renderInput={(params) => <TextField {...params} size="small" label={t('bulkEdit.addField')} />}
          sx={{ maxWidth: 360 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={running}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={locked || entries.length === 0}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => start(rows, { entries, values })}
        >
          {t('bulkEdit.run', { count: rows.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The values with this field's properties taken from its form's data — the same object when none changed. JsonForms
 * reports its data on every (re-)initialization, not only on input; a new object for an unchanged report would
 * render the dialog again and start the next report.
 */
function withFieldValues(current: Record<string, unknown>, spec: BulkEditFieldSpec, data: unknown): Record<string, unknown> {
  const reported = (data ?? {}) as Record<string, unknown>;
  const changed = spec.properties.filter((p) => !Object.is(current[p], reported[p]));
  return changed.length === 0 ? current : { ...current, ...Object.fromEntries(changed.map((p) => [p, reported[p]])) };
}
